import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Download, Share2, Lock } from "lucide-react";
import axios from "axios";
import Payment from "../PaymentPage/Payment";

const InspectionReportView = () => {
  const navigate = useNavigate();
  const { inspectionCode } = useParams();

  const token = localStorage.getItem("token");
  const role = localStorage.getItem("userRole");

  const reportUrl = `${process.env.REACT_APP_BACKEND_URL}/api/general-inspection/report/${inspectionCode}`;
  const isInternalUser =
    role === "Management" || role === "admin" || role === "employee";

  const [status, setStatus] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [reviewedBy, setReviewedBy] = useState(null);
  const [reviewedAt, setReviewedAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  /* ================= FETCH STATUS ================= */

  // Payment state and handler
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [showPaymentPage, setShowPaymentPage] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  // Simulate user info for payment (replace with real user data)
  const userId = localStorage.getItem("userId") || "guest";
  const userEmail = localStorage.getItem("userEmail") || "guest@example.com";
  const userPhone = localStorage.getItem("userPhone") || "9999999999";
  const userName = localStorage.getItem("userName") || "Guest User";

  // Payment handler
  const handlePayment = () => {
    setShowPaymentPage(true);
  };

  const fetchStatus = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/general-inspection/status/${inspectionCode}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const report = res.data?.data || {};
      // console.log("Fetched Report Status:", report);

      setStatus(report.status || "submitted");
      setReviewedBy(report.reviewedBy || null);
      setReviewedAt(report.reviewedAt || null);
      setRemarks(report.remarks || "");

      if (report.paymentStatus === "PAID") {
        setPaymentCompleted(true);
      }
    } catch (err) {
      console.error("Status fetch error:", err);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    if (inspectionCode) fetchStatus();
  }, [inspectionCode, token]);

  // Payment success callback
  const handlePaymentSuccess = () => {
    setShowPaymentPage(false);

    // poll status until webhook confirms
    const interval = setInterval(async () => {
      await fetchStatus();

      if (paymentCompleted) {
        clearInterval(interval);
      }
    }, 2000);
    alert("Payment successful! You can now download and share the report.");
  };

  /* ================= UPDATE STATUS ================= */

  const updateStatus = async (newStatus) => {
    if (!remarks.trim()) {
      alert("Remarks are mandatory");
      return;
    }

    try {
      setLoading(true);

      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/general-inspection/update-status/${inspectionCode}`,
        { status: newStatus, remarks },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setStatus(newStatus);
      setReviewedAt(new Date().toISOString());

      alert(`Report ${newStatus}`);

      // auto go back after approval
      setTimeout(() => navigate(-1), 1500);
    } catch (err) {
      console.error("Update status error:", err);
      alert("Failed to update report");
    } finally {
      setLoading(false);
    }
  };

  /* ================= PRINT ================= */

  const handlePrint = () => {
    const printWindow = window.open(reportUrl, "_blank");
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
      };
    } else {
      alert("Please allow popups to print the report");
    }
  };

  /* ================= SHARE ================= */

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Vehicle Inspection Report",
          text: `Inspection Report: ${inspectionCode}`,
          url: reportUrl,
        });
      } catch (err) {
        console.log(err);
      }
    } else {
      await navigator.clipboard.writeText(reportUrl);
      alert("Report link copied");
    }
  };

  /* ================= UI ================= */

  return (
    <div className="min-h-dvh flex flex-col bg-gray-100 font-satoshi">
      {/* HEADER */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-full px-4 py-3 flex items-center justify-between">
          {/* LEFT */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-full transition"
            >
              <ArrowLeft size={24} className="text-gray-700" />
            </button>

            <div>
              <h1 className="text-lg md:text-xl font-bold text-gray-800">
                Inspection Report
              </h1>
              <p className="text-xs text-gray-600">{inspectionCode}</p>
            </div>
          </div>

          {/* ACTION BUTTONS */}
          <div className="flex items-center gap-2">
            {isInternalUser && (
              <button
                onClick={handlePrint}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-semibold"
              >
                <Download size={18} /> Print
              </button>
            )}

            {/* Payment and Share logic for external users */}
            {!isInternalUser && paymentCompleted && (
              <>
                <button
                  onClick={handleShare}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm font-semibold"
                >
                  <Share2 size={18} /> Share
                </button>
                <button
                  onClick={handlePrint}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-semibold"
                >
                  <Download size={18} /> Download
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 flex bg-gray-200 overflow-hidden relative">
        <div className="w-full h-full flex-1 overflow-x-auto">
          <iframe
            src={reportUrl}
            loading="lazy"
            title="Inspection Report"
            className={`w-full h-[80vh] min-h-[400px] border-0 bg-white transition-all duration-300 ${
              !isInternalUser && !paymentCompleted
                ? "blur-sm pointer-events-none select-none"
                : ""
            }`}
          />
        </div>

        {/* LOCK OVERLAY */}
        {!isInternalUser && !paymentCompleted && (
          <div className="absolute inset-0 flex items-center justify-center z-50 px-4">
            {/* Light overlay */}
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm"></div>

            {/* Glass Card */}
            <div className="relative bg-white/90 backdrop-blur-xl rounded-2xl shadow-2xl p-10 max-w-md w-full text-center border border-white/40">
              {/* Lock Icon */}
              <div className="flex justify-center mb-6">
                <div className="bg-blue-50 p-4 rounded-full shadow-md">
                  <Lock size={32} className="text-blue-600" />
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-800 mb-3">
                Unlock Full Inspection Report
              </h2>

              <p className="text-gray-600 mb-6 text-sm">
                Securely pay once to view, download and share this report.
              </p>

              <div className="text-4xl font-extrabold text-green-600 mb-6">
                ₹200
              </div>

              <button
                disabled={isProcessingPayment}
                onClick={() => {
                  setIsProcessingPayment(true);
                  setShowPaymentPage(true);
                }}
                className="w-full bg-blue-600 hover:bg-blue-700 transition text-white font-semibold py-3 rounded-xl shadow-lg"
              >
                {isProcessingPayment ? "Opening Payment..." : "Pay & Unlock"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* PAYMENT GATEWAY */}
      {!isInternalUser && showPaymentPage && !paymentCompleted && (
        <Payment
          amount={1}
          customerId={userId}
          customerEmail={userEmail}
          customerPhone={userPhone}
          firstName={userName}
          description={`Inspection Report Payment for ${inspectionCode}`}
          paymentRoute="/api/inspection-report-payment"
          inspectionCode={inspectionCode}
          onSuccess={handlePaymentSuccess}
        />
      )}

      {/* REVIEW ACTION BAR */}
      {!fetching && isInternalUser && (
        <div className="sticky bottom-0 bg-white border-t shadow-lg px-4 py-2 z-50">
          {status !== "submitted" ? (
            <div className="max-w-7xl mx-auto flex items-start justify-between gap-6">
              {/* LEFT : REMARKS */}
              <div className="flex flex-col flex-1 max-w-3xl">
                <div className="text-xs font-semibold text-gray-500 tracking-wide uppercase">
                  Remarks
                </div>

                <div className="text-sm text-gray-800 leading-relaxed mt-1">
                  {remarks ? (
                    <p className="whitespace-pre-wrap">{remarks}</p>
                  ) : (
                    <span className="text-gray-400 italic">
                      No remarks provided
                    </span>
                  )}
                </div>
              </div>

              {/* RIGHT : STATUS INFO */}
              <div className="flex flex-col items-end text-right shrink-0">
                <div className="text-xs text-gray-500 font-bold uppercase tracking-wide mb-1">
                  Review Status
                </div>

                <div className="flex flex-col items-end text-right gap-1">
                  {/* Line 1 : Status + Reviewer */}
                  <div className="flex items-center gap-2">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-semibold w-fit ${
                        status === "approved"
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {String(status).toUpperCase()}
                    </span>

                    <span className="text-xs text-gray-700">
                      by <b>{reviewedBy?.name || "Admin"}</b>
                    </span>
                  </div>

                  <span className="text-xs text-gray-500">
                    {reviewedAt ? new Date(reviewedAt).toLocaleString() : ""}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className="max-w-6xl mx-auto flex items-center gap-4">
              <textarea
                placeholder="Enter inspection remarks (mandatory)"
                className="flex-1 border rounded-md p-2.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
              />

              <div className="flex gap-2">
                <button
                  disabled={loading}
                  onClick={() => updateStatus("approved")}
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 font-medium"
                >
                  Approve
                </button>

                <button
                  disabled={loading}
                  onClick={() => updateStatus("rejected")}
                  className="px-6 py-2 border border-red-500 text-red-600 rounded-md hover:bg-red-50 disabled:opacity-50 font-medium"
                >
                  Reject
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InspectionReportView;
